import React from 'react';
import { Button, Form, Input, DatePicker, Typography, Select, Card, Space, Alert } from 'antd';
import { FileTextOutlined, EnvironmentOutlined, CalendarOutlined } from '@ant-design/icons';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { 
  setIncidentDate, 
  setAccidentLocation, 
  setIncidentType, 
  setWeatherConditions,
  setAdditionalSafetyNotes,
  setRoadConditions,
  setWitnessInfo,
  IncidentType
} from '../../../redux/reducers/incidentDetailsSlice';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

interface IncidentDetailsStepProps {
  onNext: () => void;
  onPrevious: () => void;
  isFirstStep: boolean;
  isLastStep: boolean;
}

const IncidentDetailsStep: React.FC<IncidentDetailsStepProps> = ({ 
  onNext, 
  onPrevious 
}) => {
  const dispatch = useAppDispatch();
  const incidentDetails = useAppSelector((state) => state.incidentDetails);
  const operatorInfo = useAppSelector((state) => state.operatorInfo);
  const [form] = Form.useForm();

  const handleSubmit = (values: Record<string, any>) => {
    // Save all form values to Redux
    if (values.dateTime) {
      dispatch(setIncidentDate(values.dateTime.toDate()));
    }
    if (values.location) {
      dispatch(setAccidentLocation(values.location));
    }
    if (values.incidentType) {
      dispatch(setIncidentType(values.incidentType));
    }
    if (values.weatherConditions) {
      dispatch(setWeatherConditions(values.weatherConditions));
    }
    if (values.roadConditions) {
      dispatch(setRoadConditions(values.roadConditions));
    }
    if (values.description) {
      dispatch(setAdditionalSafetyNotes(values.description));
    }
    
    // Save witness info if provided
    if (values.witnessName || values.witnessPhone || values.witnessStatement) {
      dispatch(setWitnessInfo({
        name: values.witnessName || '',
        phone: values.witnessPhone || '',
        statement: values.witnessStatement || ''
      }));
    }

    onNext();
  };

  // Set initial values from Redux state
  const initialValues = {
    dateTime: incidentDetails.incidentDate ? dayjs(incidentDetails.incidentDate) : dayjs(),
    location: incidentDetails.accidentLocationDescription,
    incidentType: incidentDetails.incidentType,
    weatherConditions: incidentDetails.weatherConditions,
    roadConditions: incidentDetails.roadConditions,
    description: incidentDetails.additionalSafetyNotes,
    witnessName: incidentDetails.witnessName,
    witnessPhone: incidentDetails.witnessPhone,
    witnessStatement: incidentDetails.witnessStatement
  };

  return (
    <div className="step-container">
      <Title level={3}>Incident Details</Title>
      <Text type="secondary">Provide comprehensive details about the incident</Text>

      {operatorInfo.operatorInjured && (
        <Alert
          message="Injury Reported"
          description="Since an injury was reported, please provide detailed information about the incident circumstances."
          type="warning"
          showIcon
          style={{ margin: '20px 0' }}
        />
      )}

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={initialValues}
        style={{ marginTop: 20 }}
      >
        <Card title={<><CalendarOutlined /> When & Where</>} style={{ marginBottom: 16 }}>
          <Form.Item
            label="Date & Time of Incident"
            name="dateTime"
            rules={[{ required: true, message: 'Please select date and time' }]}
          >
            <DatePicker 
              showTime 
              size="large" 
              style={{ width: '100%' }}
              placeholder="Select date and time"
              format="YYYY-MM-DD HH:mm"
              disabledDate={(current) => current && current > dayjs().endOf('day')}
            />
          </Form.Item>

          <Form.Item
            label="Incident Location"
            name="location"
            rules={[{ required: true, message: 'Please enter location' }]}
          >
            <Input 
              size="large" 
              prefix={<EnvironmentOutlined />}
              placeholder="Enter incident location (e.g., 123 Main St, City, State)"
            />
          </Form.Item>
        </Card>

        <Card title="Incident Information" style={{ marginBottom: 16 }}>
          <Form.Item
            label="Incident Type"
            name="incidentType"
            rules={[{ required: true, message: 'Please select incident type' }]}
          >
            <Select size="large" placeholder="Select incident type">
              {Object.values(IncidentType).map(type => (
                <Option key={type} value={type}>{type}</Option>
              ))}
            </Select>
          </Form.Item>

          <Space style={{ width: '100%' }} size={16}>
            <Form.Item
              label="Weather Conditions"
              name="weatherConditions"
              rules={[{ required: true, message: 'Please select weather conditions' }]}
              style={{ flex: 1 }}
            >
              <Select size="large" placeholder="Select weather">
                <Option value="Clear">Clear</Option>
                <Option value="Rainy">Rainy</Option>
                <Option value="Snowy">Snowy</Option>
                <Option value="Foggy">Foggy</Option>
                <Option value="Windy">Windy</Option>
                <Option value="Stormy">Stormy</Option>
                <Option value="Other">Other</Option>
              </Select>
            </Form.Item>

            <Form.Item
              label="Road Conditions"
              name="roadConditions"
              rules={[{ required: true, message: 'Please select road conditions' }]}
              style={{ flex: 1 }}
            >
              <Select size="large" placeholder="Select road conditions">
                <Option value="Dry">Dry</Option>
                <Option value="Wet">Wet</Option>
                <Option value="Icy">Icy</Option>
                <Option value="Snow Covered">Snow Covered</Option>
                <Option value="Muddy">Muddy</Option>
                <Option value="Under Construction">Under Construction</Option>
                <Option value="Other">Other</Option>
              </Select>
            </Form.Item>
          </Space>

          <Form.Item
            label="Incident Description"
            name="description"
            rules={[
              { required: true, message: 'Please describe the incident' },
              { min: 20, message: 'Description must be at least 20 characters' }
            ]}
          >
            <TextArea 
              rows={6} 
              placeholder="Provide a detailed description of what happened, including the sequence of events leading up to the incident..."
              showCount
              maxLength={2000}
            />
          </Form.Item>
        </Card>

        <Card title="Witness Information (Optional)" style={{ marginBottom: 16 }}>
          <Form.Item
            label="Witness Name"
            name="witnessName"
          >
            <Input 
              size="large" 
              placeholder="Enter witness full name"
            />
          </Form.Item>

          <Form.Item
            label="Witness Phone"
            name="witnessPhone"
          >
            <Input 
              size="large" 
              placeholder="Enter witness phone number"
            />
          </Form.Item>

          <Form.Item
            label="Witness Statement"
            name="witnessStatement"
          >
            <TextArea 
              rows={3} 
              placeholder="Enter witness statement or observations..."
              showCount
              maxLength={1000}
            />
          </Form.Item>
        </Card>

        <div className="step-actions" style={{ marginTop: 40 }}>
          <Button onClick={onPrevious}>
            Previous
          </Button>
          <Button 
            type="primary" 
            htmlType="submit"
            icon={<FileTextOutlined />}
          >
            Continue to Review
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default IncidentDetailsStep;